import memoizeOne from 'memoize-one';
import { useEffect, useState } from 'react';

import type { COMMERCE_BACKEND_CCP, COMMERCE_BACKEND_HAMS } from '@atlassian/commerce-ui/rollout';
import { fetchCommerceRolloutInformation } from '@atlassian/commerce-ui/rollout';

import { cfetch } from '@confluence/network';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

export const CONFLUENCE_PRODUCT_KEY = 'confluence.ondemand';

export type CommerceBackendName = typeof COMMERCE_BACKEND_HAMS | typeof COMMERCE_BACKEND_CCP | null;

export type UseCommerceBackendNameResponse = {
	commerceBackendName: CommerceBackendName;
	loading: boolean;
	error: Error | null;
};

const memoizedFetchCommerceRolloutInformation = memoizeOne(
	fetchCommerceRolloutInformation,
	([{ cloudId: newCloudId }], [{ cloudId: lastCloudId }]) => {
		return newCloudId === lastCloudId;
	},
);

export const useCommerceBackendName = (skip: boolean = false): UseCommerceBackendNameResponse => {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);
	const [commerceBackendName, setCommerceBackendName] = useState<CommerceBackendName>(null);

	const { cloudId } = useSessionData();
	if (!cloudId) {
		skip = true;
	}

	useEffect(() => {
		if (skip) {
			return;
		}
		const loadCommerceBackendName = async () => {
			try {
				const { payload } = await memoizedFetchCommerceRolloutInformation(
					{
						cloudId,
						productKey: CONFLUENCE_PRODUCT_KEY,
					},
					{ fetch: cfetch },
				);
				setCommerceBackendName(payload?.commerceBackend ?? null);
			} catch (error) {
				error.message = `useCommerceBackendName: ${error.message}`;
				setError(error);
			} finally {
				setLoading(false);
			}
		};

		void loadCommerceBackendName();
	}, [cloudId, skip]);

	return {
		commerceBackendName: skip ? null : commerceBackendName,
		loading: skip ? false : loading,
		error: skip ? null : error,
	};
};
